// src/pages/Coleta/detalhe/DialogosDetalhe.jsx
import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Slider,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { rotuloDiluicao } from '../../../utils/coleta';

const MARCAS_DILUICAO = [0, 1, 2, 3, 4, 5, 6].map((n) => ({ value: n, label: n === 0 ? '0' : `10⁻${n}` }));

/**
 * Diluição do experimento. A contagem exibida soma a diluição atual ao resultado do
 * modelo, então ajustar aqui corrige o número sem reprocessar.
 */
export function DiluicaoDialog({ open, valorAtual, onClose, onSalvar }) {
    const [valor, setValor] = useState(0);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');

    useEffect(() => {
        if (!open) return;
        setValor(Number.isFinite(Number(valorAtual)) ? Number(valorAtual) : 0);
        setErro('');
    }, [open, valorAtual]);

    const salvar = async () => {
        setSalvando(true);
        setErro('');
        try {
            await onSalvar(valor);
        } catch (e) {
            setErro(e?.response?.data?.message || 'Não foi possível salvar a diluição.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Dialog open={open} onClose={salvando ? undefined : onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 700 }}>Diluição da amostra</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        A contagem é corrigida pela diluição; o resultado do modelo não precisa ser reprocessado.
                    </Typography>
                    <Typography fontWeight={700} textAlign="center">{rotuloDiluicao(valor)}</Typography>
                    <Slider
                        value={valor}
                        onChange={(_, v) => setValor(v)}
                        min={0}
                        max={6}
                        step={1}
                        marks={MARCAS_DILUICAO}
                        sx={{ mx: 1 }}
                    />
                    {erro && <Alert severity="error">{erro}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={salvando} color="inherit">Cancelar</Button>
                <Button onClick={salvar} variant="contained" disableElevation disabled={salvando}>
                    {salvando ? 'Salvando...' : 'Salvar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/** Exclusão da amostra: o motivo é obrigatório e fica registrado. */
export function ExcluirColetaDialog({ open, identificacao, onClose, onConfirmar }) {
    const [motivo, setMotivo] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');

    useEffect(() => {
        if (!open) return;
        setMotivo('');
        setErro('');
    }, [open]);

    const confirmar = async () => {
        setSalvando(true);
        setErro('');
        try {
            await onConfirmar(motivo.trim());
        } catch (e) {
            setErro(e?.response?.data?.message || 'Não foi possível excluir a amostra.');
            setSalvando(false);
        }
    };

    return (
        <Dialog open={open} onClose={salvando ? undefined : onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 700 }}>Excluir {identificacao}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        A amostra sai do histórico e do gráfico. A exclusão fica registrada com o motivo.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={2}
                        label="Motivo da exclusão"
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                    />
                    {erro && <Alert severity="error">{erro}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={salvando} color="inherit">Cancelar</Button>
                <Button onClick={confirmar} variant="contained" color="error" disableElevation disabled={salvando || !motivo.trim()}>
                    {salvando ? 'Excluindo...' : 'Excluir'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
